import { useEffect, useRef } from "react";

/**
 * Simple HTML5 canvas signature pad. Emits a data URL via onChange whenever the
 * user lifts their pointer. `value` is the data URL string; an empty/missing
 * value clears the canvas.
 */
export default function SignaturePad({ value, onChange, testId = "signature-pad" }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const last = useRef({ x: 0, y: 0 });

    // Initialize canvas size & rehydrate from `value`
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const resize = () => {
            const ratio = window.devicePixelRatio || 1;
            const { width, height } = canvas.getBoundingClientRect();
            canvas.width = width * ratio;
            canvas.height = height * ratio;
            const ctx = canvas.getContext("2d");
            ctx.scale(ratio, ratio);
            ctx.lineWidth = 2;
            ctx.lineCap = "round";
            ctx.strokeStyle = "#0a1628";
            if (value) {
                const img = new Image();
                img.onload = () => ctx.drawImage(img, 0, 0, width, height);
                img.src = value;
            }
        };
        resize();
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // If parent clears the value, wipe the canvas
    useEffect(() => {
        if (value) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const { width, height } = canvas.getBoundingClientRect();
        ctx.clearRect(0, 0, width, height);
    }, [value]);

    const pos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const t = e.touches?.[0];
        const x = (t ? t.clientX : e.clientX) - rect.left;
        const y = (t ? t.clientY : e.clientY) - rect.top;
        return { x, y };
    };

    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        last.current = pos(e);
    };

    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const p = pos(e);
        ctx.beginPath();
        ctx.moveTo(last.current.x, last.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        last.current = p;
    };

    const end = (e) => {
        if (!drawing.current) return;
        e?.preventDefault?.();
        drawing.current = false;
        const data = canvasRef.current.toDataURL("image/png");
        onChange(data);
    };

    return (
        <div className="space-y-2" data-testid={testId}>
            <canvas
                ref={canvasRef}
                className="w-full h-44 sm:h-56 rounded-lg bg-white border border-[#D4AF37]/35 cursor-crosshair touch-none"
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={end}
                onMouseLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
                data-testid="signature-canvas"
            />
            <div className="text-center">
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className="text-xs gold-text hover:text-[#F3E5AB] transition tracking-wide"
                    data-testid="signature-clear-btn"
                >
                    Clear signature
                </button>
            </div>
        </div>
    );
}
